import React from 'react';
import { Box, CircularProgress, Typography, keyframes } from '@mui/material';

// Simple, readable animations
const pulse = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const dotFlash = keyframes`
  0%, 80%, 100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
`;

interface SimpleLoaderProps {
  message?: string;
  submessage?: string;
  size?: number;
}

const SimpleLoader: React.FC<SimpleLoaderProps> = ({ 
  message = "Loading", 
  submessage = "Please wait",
  size = 60 
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        gap: 2,
        padding: 3
      }}
    >
      <CircularProgress
        size={size}
        thickness={4}
        sx={{
          color: 'primary.main',
          animation: `${bounce} 1.5s infinite ease-in-out`
        }}
      />

      <Typography
        variant="h6"
        sx={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontWeight: 500,
          animation: `${pulse} 1.5s infinite ease-in-out`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <span>{message}</span>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Box
            component="span"
            sx={{
              animation: `${dotFlash} 1.5s infinite ease-in-out`,
              animationDelay: '0s',
            }}
          >
            .
          </Box>
          <Box
            component="span"
            sx={{
              animation: `${dotFlash} 1.5s infinite ease-in-out`,
              animationDelay: '0.3s',
            }}
          >
            .
          </Box>
          <Box
            component="span"
            sx={{
              animation: `${dotFlash} 1.5s infinite ease-in-out`,
              animationDelay: '0.6s',
            }}
          >
            .
          </Box>
        </Box>
      </Typography>
      
      {submessage && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            maxWidth: '300px'
          }}
        >
          {submessage}
        </Typography>
      )}
    </Box>
  );
};

export default SimpleLoader;